<template>
    <div>
        <AdvanceFourSectionLayout
            :isSalaryCost="isSalaryCost"
            :invoice-data="invoiceData"
            :payment-data="paymentData"
            :salary-data="data.statisticsInfo"
            :total-income="invoiceTotal"
            :total-payout="payoutTotal"
            @switch="onSwitchSalaryCategory"
        />
        <div class="estimate-advance-layout ah-container" style="margin-top: 10px; margin-bottom: 25px">
            <AdvanceTable
                type="invoice"
                :is-loading="isLoading"
                :invoiceData="invoiceData"
                :paymentData="paymentData"
                :estimatedInvoiceAmount="data.estimatedInvoiceAmount"
                :estimatedPaymentAmount="data.estimatedPaymentAmount"
                :estimatedProjectAmount="data.estimatedProjectAmount"
                @add="onAdd"
                @delete="$event => onDelete($event.index, $event.target)"
            />
            <div class="print-table">
                <TitlePrint :year="year" form-type="明細編列" />
                <AdvanceFourSectionPrintTable
                    :isSalaryCost="isSalaryCost"
                    :invoice-data="invoiceData"
                    :payment-data="paymentData"
                    :total-income="invoiceTotal"
                    :total-payout="payoutTotal"
                    :salary-data="data.statisticsInfo"
                />
                <div class="sub-title">{{ year }} 年度預估營業總收入</div>
                <AdvancePrintTable type="invoice" :data="invoiceData" :estimatedAmount="data.estimatedInvoiceAmount" :estimatedProjectAmount="data.estimatedProjectAmount" />
                <div class="spacer"></div>
                <div class="sub-title">{{ year }} 年度預估營業總支出</div>
                <AdvancePrintTable type="payment" :data="paymentData" :estimatedAmount="data.estimatedPaymentAmount" />
            </div>
        </div>
    </div>
</template>

<script>
import _ from 'lodash';
import TitlePrint from '@/components/dashboard/Finance/Analysis/EstimatePage/TitlePrint.vue';
import AdvanceTable from '@/components/dashboard/Finance/Analysis/EstimatePage/AdvanceTable.vue';
import AdvancePrintTable from '@/components/dashboard/Finance/Analysis/EstimatePage/AdvancePrintTable.vue';
import AdvanceFourSectionLayout from '@/components/dashboard/Finance/Analysis/EstimatePage/AdvanceFourSectionLayout.vue';
import AdvanceFourSectionPrintTable from '@/components/dashboard/Finance/Analysis/EstimatePage/AdvanceFourSectionPrintTable.vue';

export default {
    components: {
        TitlePrint,
        AdvanceTable,
        AdvancePrintTable,
        AdvanceFourSectionLayout,
        AdvanceFourSectionPrintTable,
    },
    props: {
        isLoading: Boolean,
        year: {
            type: Number,
            default: 0,
        },
        data: {
            type: Object,
            default: () => {},
        },
        actuallyInvoiceAmountList: {
            type: Array,
            default: () => [],
        },
        actuallyPaymentAmountList: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            invoiceData: [],
            paymentData: [],

            isSalaryCost: true,
        };
    },
    watch: {
        data() {
            this.init();
        },
    },
    computed: {
        // INVOICE
        invoiceTotal() {
            return _.sumBy(this.invoiceData, (o) => o.actuallyAmount + o.nonProjectAmount + o.projectAmount + o.futureAmt);
        },

        // PAYMENT
        payoutTotal() {
            return _.sumBy(this.paymentData, (o) => o.actuallyAmount + o.nonProjectAmount + o.projectAmount + o.futureAmt);
        },
    },
    mounted() {
        this.init();
    },
    methods: {
        init(skipAdd) {
            this.invoiceData = _.map(this.actuallyInvoiceAmountList, (o) => ({
                ...o,
                nonProjectAmount: 0,
                projectAmount: 0,
                futureAmt: 0,
            }));

            this.paymentData = _.map(this.actuallyPaymentAmountList, (o) => ({
                ...o,
                nonProjectAmount: 0,
                projectAmount: 0,
                futureAmt: 0,
            }));

            if (!skipAdd) {
                // 自動產生「人事薪資預估」項目
                this.paymentData.push({
                    custom: true,
                    iteM_NAM: '人事薪資預估',
                    actuallyAmount: 0,
                    nonProjectAmount: 0,
                    projectAmount: 0,
                    futureAmt: 0,
                    grP_NAM: null,
                });
            }
        },
        onAdd(addTarget) {
            this[addTarget].push({
                custom: true,
                iteM_NAM: '新增項目',
                actuallyAmount: 0,
                nonProjectAmount: 0,
                projectAmount: 0,
                futureAmt: 0,
                grP_NAM: addTarget === 'paymentData' ? null : 4,
            });
        },
        onDelete(index, addTarget) {
            this[addTarget].splice(index, 1);
        },

        getTableData() {
            const invoiceData = _.map(this.invoiceData, (o) => ({
                custom: o.custom,
                iteM_NAM: o.iteM_NAM,
                nonProjectAmount: o.nonProjectAmount,
                projectAmount: o.projectAmount,
                futureAmt: o.futureAmt,
            }));
            const paymentData = _.map(this.paymentData, (o) => ({
                custom: o.custom,
                iteM_NAM: o.iteM_NAM,
                grP_NAM: o.grP_NAM,
                nonProjectAmount: o.nonProjectAmount,
                projectAmount: o.projectAmount,
                futureAmt: o.futureAmt,
            }));

            return { invoiceData, paymentData };
        },

        loadLocalData(localData) {
            this.init(true);

            // 載入項目自編資料
            this.invoiceData = _.map(this.invoiceData, (o) => {
                const FOUND = _.find(localData.data.invoiceData, ['iteM_NAM', o.iteM_NAM]);

                return {
                    ...o,
                    nonProjectAmount: FOUND.nonProjectAmount,
                    projectAmount: FOUND.projectAmount,
                    futureAmt: FOUND.futureAmt,
                };
            });
            this.paymentData = _.map(this.paymentData, (o) => {
                const FOUND = _.find(localData.data.paymentData, ['iteM_NAM', o.iteM_NAM]);

                return {
                    ...o,
                    nonProjectAmount: FOUND.nonProjectAmount,
                    projectAmount: FOUND.projectAmount,
                    futureAmt: FOUND.futureAmt,
                };
            });

            // 載入自行新增資料
            const customInvoiceData = _.chain(localData.data.invoiceData)
                .filter(['custom', true])
                .map((o) => ({ ...o, actuallyAmount: 0 }))
                .value();
            const customPaymentData = _.chain(localData.data.paymentData)
                .filter(['custom', true])
                .map((o) => ({ ...o, actuallyAmount: 0 }))
                .value();

            this.invoiceData.push(...customInvoiceData);
            this.paymentData.push(...customPaymentData);
        },
        onSwitchSalaryCategory() {
            this.isSalaryCost = !this.isSalaryCost;
        },
    },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.estimate-advance-layout {
    .sub-title {
        color: $main-color;
        border-bottom: 1px solid #eee;
        padding-bottom: 10px;
    }
    .spacer {
        margin-top: 50px;
    }

    .el-table__header th {
        vertical-align: top;
    }
    @media screen {
        .print-table {
            display: none;
        }
    }
}
</style>
